import { useLocation, useNavigate } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { useTenant, type Tenant } from '@/contexts/TenantContext'
import { ErrorState } from '@/components/ErrorState'
import { resolveTenantReturnPath } from '@/lib/tenantReturnPath'
import { BrandLogo } from '@/components/BrandLogo'

export function TenantSelectPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { availableTenants, setCurrentTenant, isLoading, isError, refetchTenants } = useTenant()

  const activeTenants = availableTenants.filter(t => t.status === 'Active')
  const inactiveTenants = availableTenants.filter(t => t.status !== 'Active')

  const requestedPath = (location.state as { from?: string } | null)?.from
  const returnTo = resolveTenantReturnPath(requestedPath)

  const handleSelect = (tenant: Tenant) => {
    if (tenant.status !== 'Active') return
    setCurrentTenant(tenant)
    navigate(returnTo, { replace: true })
  }

  return (
    <div className="min-h-screen page-canvas px-8 pt-16">
      <div className="w-full max-w-5xl mx-auto space-y-8">
        <BrandLogo variant="icon" className="h-12 w-auto" />
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Building2 className="h-7 w-7 shrink-0" />
            <h1 className="text-2xl font-bold">Select a tenant to continue.</h1>
          </div>
          <p className="text-sm text-muted-foreground pl-10">
            Choose the organisation you want to work with. You can switch the tenants at any time.
          </p>
        </div>

        {isLoading && (
          <div className="text-center py-8 text-muted-foreground">Loading tenants...</div>
        )}
        {!isLoading && isError && availableTenants.length === 0 && (
          <ErrorState
            title="Failed to load tenants"
            description="Something went wrong while loading your tenants. Please try again."
            testId="tenants-error"
            onRetry={refetchTenants}
          />
        )}
        {!isLoading && !isError && availableTenants.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No tenants available. Contact your administrator for access.
          </div>
        )}

        {!isLoading && availableTenants.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeTenants.map(tenant => (
              <button
                key={tenant.id}
                onClick={() => handleSelect(tenant)}
                className="flex items-center gap-4 rounded-lg border bg-card p-4 text-left hover:shadow-sm transition-shadow"
              >
                <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center shrink-0">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                </div>
                <span className="font-semibold text-foreground">{tenant.name}</span>
              </button>
            ))}
            {inactiveTenants.map(tenant => (
              <div
                key={tenant.id}
                className="flex items-center gap-4 rounded-lg border bg-card p-4 opacity-50 cursor-not-allowed"
              >
                <div className="h-12 w-12 rounded-md bg-muted flex items-center justify-center shrink-0">
                  <Building2 className="h-6 w-6 text-muted-foreground" />
                </div>
                <span className="font-semibold text-foreground">{tenant.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
